// Corner and loop snapping for a run of axis-aligned walls or beams.
// Positions are {x, z}, axes are "x" | "z", signs are 1 | -1, t is the corner square size.
// The chain is the first member: start is the center of its first t x t square.

const EPS = 0.002;
const lateralOf = (axis) => (axis === "x" ? "z" : "x");

// Start-square centers for the next member.
// Outer: the new member covers the corner square past the previous end.
// Inner: the previous member keeps the corner square and the new one butts into its side.
export function startCorners(prev, axis, sign, t) {
    const outer = {x: prev.end.x, z: prev.end.z};
    const inner = {x: prev.end.x, z: prev.end.z};
    outer[prev.axis] += prev.sign * t / 2;
    inner[prev.axis] -= prev.sign * t / 2;
    inner[axis] += sign * t;
    return {outer, inner};
}

// A member perpendicular to the first one closes the loop on one of two centerlines:
// "butt" runs into the side of the first member, "cover" runs across its start face.
function closingKind(chain, centerline, t) {
    const butt = chain.start[chain.axis];
    if (Math.abs(centerline - butt) < EPS) {
        return "butt";
    }
    if (Math.abs(centerline - (butt - chain.sign * t)) < EPS) {
        return "cover";
    }
    return null;
}

function headsToStart(chain, from, axis, sign) {
    return axis !== chain.axis && sign === Math.sign(chain.start[axis] - from[axis]);
}

// The cursor picks among the allowed styles. On the member that returns to the start, only a
// corner that lands on a closing centerline is offered, so the loop can always be closed.
export function chooseStartCorner(chain, prev, axis, sign, cursor, t, allowed = ["outer", "inner"]) {
    const corners = startCorners(prev, axis, sign, t);
    let styles = allowed.slice();
    if (chain && prev.axis === chain.axis && headsToStart(chain, prev.end, axis, sign)) {
        const closing = styles.filter((style) => closingKind(chain, corners[style][prev.axis], t) !== null);
        if (closing.length > 0) {
            styles = closing;
        }
    }
    const lateral = prev.axis;
    styles.sort((a, b) => Math.abs(cursor[lateral] - corners[a][lateral]) - Math.abs(cursor[lateral] - corners[b][lateral]));
    return {style: styles[0], center: corners[styles[0]]};
}

// End coordinates for a member running back parallel to the first one.
// Each leaves a corner of an allowed style from which the next member closes the loop.
// Ending on the first member's start face works for both styles; one t inside it needs an outer turn.
export function returnTargets(chain, origin, axis, sign, t, allowed = ["outer", "inner"]) {
    if (!chain || axis !== chain.axis || sign !== -chain.sign) {
        return [];
    }
    const lateral = lateralOf(axis);
    if (Math.abs(origin[lateral] - chain.start[lateral]) <= 2 * t) {
        return [];
    }
    const face = chain.start[axis] - chain.sign * t / 2;
    return allowed.includes("outer") ? [face, face + chain.sign * t] : [face];
}

// Where the closing member stops: on the side face of the first member for a butt,
// flush with its far face for a cover. side is half the first member's width.
export function loopEnd(chain, origin, axis, sign, t, side = t / 2) {
    if (!chain || !headsToStart(chain, origin, axis, sign)) {
        return null;
    }
    const kind = closingKind(chain, origin[chain.axis], t);
    if (kind === null) {
        return null;
    }
    const near = chain.start[axis] - sign * side;
    return {coord: kind === "butt" ? near : near + 2 * sign * side, kind};
}

// True when a member ending at endCoord sits on a face of one of `kinds` that spans its centerline.
// lines are face lines {start, end, snapKind} at constant `axis`. Flush faces that touch are one
// face, so a wall corner (the end of one wall flush with the side of the next) counts like the
// middle of a wall. A lone wall or beam face under 20 cm is just its end; a post face is the post's width.
export function landsOnMember(endCoord, centerline, axis, lines, kinds = ["wall", "beam"], slop = 0.02) {
    const other = lateralOf(axis);
    const faces = lines
        .filter((line) => kinds.includes(line.snapKind) && Math.abs(line.start[axis] - endCoord) <= slop)
        .map((line) => ({
            min: Math.min(line.start[other], line.end[other]),
            max: Math.max(line.start[other], line.end[other]),
            post: line.snapKind === "pillar"
        }))
        .sort((a, b) => a.min - b.min);

    let merged = null;
    for (const face of faces) {
        if (merged !== null && face.min <= merged.max + slop) {
            merged.max = Math.max(merged.max, face.max);
            merged.post = merged.post || face.post;
            continue;
        }
        if (merged !== null && centerline >= merged.min && centerline <= merged.max) {
            break;
        }
        merged = {...face};
    }
    if (merged === null || centerline < merged.min || centerline > merged.max) {
        return false;
    }
    return merged.max - merged.min >= (merged.post ? 0.08 : 0.2);
}

// One end snap for the member being drawn. A loop close wins, otherwise the nearest
// of the return targets and the given face coordinates. kind is set only when it closes the loop.
export function pickChainEnd(chain, origin, rawEnd, axis, sign, t, options = {}) {
    const tolerance = options.tolerance ?? Infinity;
    const minSegment = options.minSegment ?? 0;
    const reaches = (coord) => (coord - origin[axis]) * sign >= minSegment && Math.abs(rawEnd[axis] - coord) <= tolerance;

    const loop = loopEnd(chain, origin, axis, sign, t, options.side);
    if (loop !== null && reaches(loop.coord)) {
        return loop;
    }

    const candidates = returnTargets(chain, origin, axis, sign, t, options.styles).concat(options.faces || []).filter(reaches);
    if (candidates.length === 0) {
        return null;
    }
    candidates.sort((a, b) => Math.abs(rawEnd[axis] - a) - Math.abs(rawEnd[axis] - b));
    return {coord: candidates[0], kind: null};
}
